import type { SxProps, Theme } from '@mui/material';

/** Accent used for wallet / secondary highlights (matches Luxe mock mint). */
export const BRAND_SECONDARY = '#00e6c3';

/** Obsidian HTML mock — surface-container-lowest */
export const BRAND_SURFACE_CONTAINER_LOWEST = '#000000';
/** Obsidian HTML mock — surface-container-low */
export const BRAND_SURFACE_CONTAINER_LOW = '#131313';
/** Obsidian HTML mock — surface-container */
export const BRAND_SURFACE_CONTAINER = '#1a1919';

/** LUXE sample — nav / headline gold */
export const BRAND_LUXE_GOLD = '#FFC107';
/** LUXE sample — primary text gold */
export const BRAND_LUXE_PRIMARY = '#ffc965';
/** Obsidian mock — primary-container (solid gold CTA) */
export const BRAND_PRIMARY_CONTAINER = '#feb700';
/** Obsidian mock — on-primary-fixed (text on gold buttons) */
export const BRAND_ON_PRIMARY_FIXED = '#392700';
/** Softer gold for modal/icons/chips (less harsh than solid BRAND_LUXE_PRIMARY). */
export const BRAND_MODAL_ACCENT = '#e8c27d';
export const BRAND_MODAL_ACCENT_ICON = 'rgba(232, 194, 125, 0.92)';
export const BRAND_MODAL_ACCENT_HOVER = '#dfbc75';
export const BRAND_MODAL_ACCENT_CHIP_BG = 'rgba(232, 194, 125, 0.16)';
export const BRAND_MODAL_ACCENT_CHIP_BORDER = 'rgba(232, 194, 125, 0.32)';
export const BRAND_MODAL_ACCENT_LIST_SELECTED = 'rgba(232, 194, 125, 0.14)';
/** LUXE sample — muted nav links */
export const BRAND_LUXE_MUTED = '#a0a0a0';
/** LUXE sample — page background */
export const BRAND_LUXE_BG = '#0e0e0e';
/** LUXE sample — secondary mint */
export const BRAND_LUXE_SECONDARY = '#00fdc1';
/** LUXE sample — error */
export const BRAND_LUXE_ERROR = '#ff7351';

/** Scroll target on brand campaigns list when returning from contest manage. */
export const BRAND_CAMPAIGNS_CONTESTS_SECTION_ID = 'brand-contests-section';

export type BrandCampaignsTab = 'ugc' | 'contest' | 'audition';

export const BRAND_CAMPAIGNS_TAB_PARAM = 'tab';

/** @deprecated Use `?tab=contest` on campaigns route */
export type BrandCampaignsScrollSection = 'contests';

export const BRAND_CAMPAIGNS_SCROLL_STATE_KEY = 'scrollToSection';
/** LUXE sample — surface-container-high */
export const BRAND_SURFACE_CONTAINER_HIGH = '#201f1f';

/** surface-container-highest */
export const BRAND_SURFACE_CONTAINER_HIGHEST = '#262626';
/** outline-variant */
export const BRAND_OUTLINE_VARIANT = '#484847';

/**
 * Shared MUI OutlinedInput look for brand modals: real `fieldset` border (no inset “underline”),
 * `overflow: hidden` for clean corners, comfortable horizontal padding.
 */
export const brandModalOutlinedInputSx: SxProps<Theme> = {
    width: '100%',
    maxWidth: '100%',
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        backgroundColor: BRAND_SURFACE_CONTAINER_HIGHEST,
        color: '#fff',
        overflow: 'hidden',
        '& fieldset': {
            borderColor: BRAND_OUTLINE_VARIANT,
            borderWidth: 1,
        },
        '&:hover fieldset': {
            borderColor: 'rgba(255,255,255,0.32)',
        },
        '&.Mui-focused fieldset': {
            borderColor: BRAND_MODAL_ACCENT,
            borderWidth: 1,
        },
        '&.Mui-focused': {
            boxShadow: 'none',
        },
        // icon buttons inside pickers (calendar/clock icons)
        '& .MuiIconButton-root': {
            color: BRAND_LUXE_MUTED,
        },
    },
    '& .MuiOutlinedInput-input': {
        paddingLeft: '16px',
        paddingRight: '16px',
        paddingTop: '13px',
        paddingBottom: '13px',
        color: '#fff',
        '&::placeholder': { color: BRAND_LUXE_MUTED, opacity: 1 },
    },
    '& .MuiInputLabel-root': {
        color: BRAND_LUXE_MUTED,
        '&.Mui-focused': { color: BRAND_MODAL_ACCENT },
    },
    '& .MuiInputAdornment-root .MuiSvgIcon-root': {
        color: BRAND_LUXE_MUTED,
    },
};

/** Outer gutter so full-bleed inputs don’t hug the modal edge. */
export const brandModalFieldGutterSx: SxProps<Theme> = {
    px: { xs: 0.5, sm: 0.75 },
    boxSizing: 'border-box',
};

/** Mesh background layers on top of theme base. */
export const brandMeshBackground = (baseBg: string) => ({
    backgroundColor: baseBg,
    backgroundImage: `
        radial-gradient(at 0% 0%, rgba(255, 201, 101, 0.06) 0px, transparent 50%),
        radial-gradient(at 100% 100%, rgba(0, 230, 195, 0.04) 0px, transparent 50%)
    `,
});
